import type { Message, Uuid } from '@liniya/api-client';
import { useCallback, useMemo, useState } from 'react';

const EMPTY: ReadonlySet<Uuid> = new Set();

/**
 * Whether this viewer may delete this message.
 *
 * The server allows only the author, in every kind of chat — there is no
 * moderator override and no "delete for me". Offering selection on a message
 * that cannot be deleted would produce a tidy list of checkboxes and then a
 * row of 403s, so the rule lives here and both clients ask the same question.
 */
export function canDeleteMessage(message: Message, viewerUserId: Uuid): boolean {
  return message.sender_user_id === viewerUserId && !message.deleted;
}

export interface MessageSelection {
  selectedIds: ReadonlySet<Uuid>;
  /** True once anything is chosen; both clients swap their header on it. */
  selecting: boolean;
  canSelect: (message: Message) => boolean;
  isSelected: (messageId: Uuid) => boolean;
  toggle: (message: Message) => void;
  clear: () => void;
}

/**
 * Choosing several messages at once, for deleting them in one confirmation.
 *
 * Kept out of both clients so they cannot drift on the part that matters: what
 * may be chosen. The set itself is ordinary local state — it belongs to the
 * open conversation and should not survive leaving it.
 */
export function useMessageSelection(viewerUserId: Uuid): MessageSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<Uuid>>(EMPTY);

  const canSelect = useCallback(
    (message: Message) => canDeleteMessage(message, viewerUserId),
    [viewerUserId],
  );

  const toggle = useCallback(
    (message: Message) => {
      if (!canDeleteMessage(message, viewerUserId)) return;
      setSelectedIds((current) => {
        const next = new Set(current);
        if (!next.delete(message.message_id)) next.add(message.message_id);
        return next;
      });
    },
    [viewerUserId],
  );

  const clear = useCallback(() => setSelectedIds(EMPTY), []);

  const isSelected = useCallback(
    (messageId: Uuid) => selectedIds.has(messageId),
    [selectedIds],
  );

  return useMemo(
    () => ({
      selectedIds,
      selecting: selectedIds.size > 0,
      canSelect,
      isSelected,
      toggle,
      clear,
    }),
    [canSelect, clear, isSelected, selectedIds, toggle],
  );
}
